import { useState, useRef, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useBooking } from '../context/BookingContext';
import { supabase } from '../lib/supabase';
import { trackEvent } from '../lib/analytics';

export default function OtpVerification() {
  const { fullName, phone, setIsVerified, setCustomerId, fading, go } = useBooking();

  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [countdown, setCountdown] = useState(30);
  const [timerKey, setTimerKey] = useState(0);
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [otpError, setOtpError] = useState('');
  const inputRefs = useRef([]);

  // Countdown — starts fresh on entering this screen or when resend is triggered
  useEffect(() => {
    setCountdown(30);
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) { clearInterval(timer); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [timerKey]);

  // Auto-focus first OTP box after the fade-in completes
  useEffect(() => {
    const t = setTimeout(() => inputRefs.current[0]?.focus(), 300);
    return () => clearTimeout(t);
  }, []);

  const handleOtpChange = (index, e) => {
    const raw = e.target.value.replace(/\D/g, '');
    if (raw.length > 1) {
      const digits = raw.slice(0, 6 - index);
      const next = [...otp];
      digits.split('').forEach((d, i) => { if (index + i < 6) next[index + i] = d; });
      setOtp(next);
      inputRefs.current[Math.min(index + digits.length, 5)]?.focus();
      return;
    }
    const digit = raw.slice(-1);
    const next = [...otp];
    next[index] = digit;
    setOtp(next);
    if (digit && index < 5) inputRefs.current[index + 1]?.focus();
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const next = [...otp];
      next[index - 1] = '';
      setOtp(next);
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!pasted) return;
    const next = Array(6).fill('');
    pasted.split('').forEach((d, i) => { next[i] = d; });
    setOtp(next);
    inputRefs.current[Math.min(pasted.length, 5)]?.focus();
  };

  const handleVerify = async () => {
    const code = otp.join('');
    if (code.length < 6) {
      setOtpError('Please enter all 6 digits');
      return;
    }
    setOtpError('');
    setVerifying(true);
    try {
      const { data, error } = await supabase.functions.invoke('otp', {
        body: { action: 'verify', phone, code },
      });
      if (!error && data?.success) {
        setCustomerId(data.customerId ?? null);
        setIsVerified(true);
        // Funnel step 2 — identity verified.
        trackEvent('login_completed', { method: 'otp' });
        go('/select-date');
        return;
      }
      setOtpError(data?.error || 'Invalid or expired code');
      setOtp(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } catch (err) {
      setOtpError('Something went wrong. Please try again.');
      setOtp(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setVerifying(false);
    }
  };

  const handleResend = async () => {
    if (countdown > 0 || resending) return;
    setOtpError('');
    setOtp(['', '', '', '', '', '']);
    setResending(true);
    try {
      const { data, error } = await supabase.functions.invoke('otp', {
        body: { action: 'send', fullName, phone },
      });
      if (error || !data?.success) {
        setOtpError(data?.error || 'Could not resend the code. Please try again.');
        return;
      }
      setTimerKey(k => k + 1);
    } catch (err) {
      setOtpError('Could not resend the code. Please try again.');
    } finally {
      setResending(false);
    }
  };

  // Guard — customer details must be present
  if (!fullName || !phone) return <Navigate to="/" replace />;

  return (
    <div className={`w-full max-w-[480px] p-px rounded-2xl bg-gradient-to-b from-[rgba(212,168,67,0.32)] via-[rgba(255,255,255,0.06)] to-[rgba(255,255,255,0.02)] card-enter transition-opacity duration-[250ms] ${fading ? 'opacity-0' : 'opacity-100'}`}>
        <div className="bg-[#0D1E35] rounded-[15px] shadow-[0_40px_80px_rgba(0,0,0,0.55)] px-7 py-8">

          <button
            type="button"
            onClick={() => go('/')}
            className="flex items-center justify-center w-8 h-8 rounded-full border border-[rgba(212,168,67,0.3)] hover:border-[rgba(212,168,67,0.65)] hover:bg-[rgba(212,168,67,0.06)] transition-[border-color,background-color] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] mb-5"
            aria-label="Go back"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
              <path d="M9 2L4 7L9 12" stroke="#D4A843" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>

          <h1 className="font-cormorant font-normal text-[1.8rem] leading-[1.1] tracking-[-0.01em] text-white mb-2">
            Enter your OTP
          </h1>
          <p className="font-dm-sans text-sm text-white/55 font-light mb-5 leading-relaxed">
            We've sent a 6-digit code by WhatsApp and SMS
          </p>

          <div className="flex items-center justify-center gap-2">
            {otp.map((digit, index) => (
              <input
                key={index}
                ref={el => { inputRefs.current[index] = el; }}
                type="text"
                inputMode="numeric"
                value={digit}
                maxLength={2}
                autoComplete={index === 0 ? 'one-time-code' : 'off'}
                onChange={e => handleOtpChange(index, e)}
                onKeyDown={e => handleOtpKeyDown(index, e)}
                onPaste={handleOtpPaste}
                className="w-10 h-12 bg-[#162840] rounded-lg border border-white/10 focus:border-[rgba(212,168,67,0.75)] focus:shadow-[0_0_0_2px_rgba(212,168,67,0.12)] text-white text-center text-[1.25rem] font-light font-dm-sans outline-none caret-[#D4A843] transition-[border-color,box-shadow] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)]"
                aria-label={`OTP digit ${index + 1}`}
              />
            ))}
          </div>

          {otpError && (
            <p className="font-dm-sans text-center text-sm text-white/55 mt-3 leading-relaxed">
              {otpError}
            </p>
          )}

          <button
            type="button"
            onClick={handleVerify}
            disabled={verifying}
            className="w-full mt-[22px] py-[14px] bg-[#C9A84C] text-[#0A1628] font-dm-sans font-medium text-[12.5px] tracking-[0.15em] uppercase rounded-full active:scale-[0.97] hover:bg-[#B8973E] transition-[transform,background-color] duration-[160ms] ease-[cubic-bezier(0.23,1,0.32,1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(201,168,76,0.5)] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0D1E35]"
          >
            {verifying ? 'Verifying...' : 'Verify OTP'}
          </button>

          <div className="flex items-center justify-center gap-1.5 mt-4">
            <span className="font-dm-sans text-sm text-white/55">Didn't receive it?</span>
            <button
              type="button"
              onClick={handleResend}
              disabled={countdown > 0 || resending}
              className={`font-dm-sans text-sm font-medium transition-colors duration-300 ${
                countdown > 0 ? 'text-white/20 cursor-not-allowed' : 'text-[#D4A843] hover:text-[#C9A84C] cursor-pointer'
              }`}
            >
              Resend OTP
            </button>
          </div>

          <p className="font-dm-sans text-center text-sm text-white/55 mt-2">
            {countdown > 0 ? `Resend available in ${countdown}s` : 'You can now resend the OTP'}
          </p>

        </div>
      </div>
  );
}
